import React, { CSSProperties, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorMessage, withFormik, FormikProps } from 'formik';
import styled from 'styled-components';

import { userSchema } from '@/services/validators/user';
import { User } from '@/types/userList';
import { FormValues } from '@/components/pages/Users/Index/UserListForm';
import { SharedButton } from '@/components/shared/Button';

type OuterProps = {
  isUpdating: boolean;
  onCancelClick: (e: MouseEvent<HTMLAnchorElement>) => void;
  onSubmit: (values: FormValues) => Promise<void>;
  recomputeRowHeights: () => void;
  style: CSSProperties;
  user: User;
};

type Values = Pick<
  User,
  | 'lastName'
  | 'firstName'
  | 'kanaLastName'
  | 'kanaFirstName'
  | 'email'
  | 'nameId'
>;

export const UserFormContents = withFormik<OuterProps, Values>({
  mapPropsToValues: ({ user }) => ({
    lastName: user.lastName,
    firstName: user.firstName,
    kanaLastName: user.kanaLastName,
    kanaFirstName: user.kanaFirstName,
    email: user.email,
    nameId: user.nameId,
  }),
  validationSchema: userSchema,
  handleSubmit: async (values, { props }) => {
    await props.onSubmit(values);
    props.recomputeRowHeights();
  },
})(
  // enhanced form
  ({
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    values,
    onCancelClick,
    recomputeRowHeights,
    isUpdating,
  }: OuterProps & FormikProps<Values>) => {
    const { t } = useTranslation();

    return (
      <StyledForm onSubmit={handleSubmit}>
        <FieldGroup>
          <Field>
            <Label>
              {t('models.user.lastName')} <Required>*</Required>
            </Label>
            <input
              disabled={true}
              name="lastName"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.lastName}
            />
            <Span>
              <ErrorMessage name="lastName" />
            </Span>
          </Field>
          <Field>
            <Label>
              {t('models.user.firstName')} <Required>*</Required>
            </Label>
            <input
              disabled={true}
              name="firstName"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.firstName}
            />
            <Span>
              <ErrorMessage name="firstName" />
            </Span>
          </Field>
        </FieldGroup>
        <FieldGroup>
          <Field>
            <Label>
              {t('models.user.kanaLastName')} <Required>*</Required>
            </Label>
            <input
              disabled={true}
              name="kanaLastName"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.kanaLastName}
            />
            <Span>
              <ErrorMessage name="kanaLastName" />
            </Span>
          </Field>
          <Field>
            <Label>
              {t('models.user.kanaFirstName')} <Required>*</Required>
            </Label>
            <input
              disabled={true}
              name="kanaFirstName"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.kanaFirstName}
            />
            <Span>
              <ErrorMessage name="kanaFirstName" />
            </Span>
          </Field>
        </FieldGroup>
        <FieldGroup>
          <Field>
            <Label>
              {t('models.user.email')} <Required>*</Required>
            </Label>
            <input
              disabled={true}
              name="email"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.email}
            />
            <Span>
              <ErrorMessage name="email" />
            </Span>
          </Field>
          <Field>
            <Label>{t('models.user.nameId')}</Label>
            <input
              name="nameId"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.nameId || ''}
            />
            <Span>
              <ErrorMessage name="nameId" />
            </Span>
          </Field>
        </FieldGroup>
        <Action>
          <A
            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
              onCancelClick(e);
              recomputeRowHeights();
            }}
          >
            {t('ui.action.cancel')}
          </A>
          <SharedButton
            disabled={isSubmitting}
            loading={isUpdating}
            type="submit"
          >
            {t('ui.action.updateNow')}
          </SharedButton>
        </Action>
      </StyledForm>
    );
  },
);

const StyledForm = styled.form`
  font-size: 1.14285714rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1em;
  margin-bottom: 1em;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;

  input {
    width: 100%;
    padding: 0.67857143em 1em;
    border: 1px solid rgba(34, 36, 38, 0.15);
    border-radius: 0.28571429rem;
    font-size: 1em;
    line-height: 1.2142em;
    background: #fff;
    color: rgba(0, 0, 0, 0.87);

    &:disabled {
      background: #fafafa;
      color: rgba(40, 40, 40, 0.3);
      pointer-events: none;
    }
  }
`;

const Action = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const A = styled.a`
  cursor: pointer;
  font-size: 12px;
  margin-right: 24px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5em;
  font-size: 12px;
  font-weight: bold;
  color: rgba(0, 0, 0, 0.82);
`;

const Required = styled.span`
  color: #db2828;
`;

const Span = styled.span`
  color: #9f3a38;
`;
